import {
  AddEstimateSectionInput,
  GetProjectEstimatesQuery,
  UpdateEstimateLineItemInput,
  UpdateEstimateLineItemMaterialsInput,
  UpdateEstimateSectionInput,
} from '@/gql/graphql';
import { produce } from 'immer';
import { OptimisticEstimateContext } from './createOptimisticEstimateMutation';

export type AddEstimateSectionOptimisticVariables = AddEstimateSectionInput & {
  publicId: string;
};

export type ReorderEstimateLineItemsOptimisticVariables = {
  sectionId: string;
  lineItemIds: string[];
};

export type ReorderEstimateSectionsOptimisticVariables = {
  sectionIds: string[];
};

const findEstimate = (
  draft: GetProjectEstimatesQuery,
  estimateId: string
): GetProjectEstimatesQuery['projectById']['estimates'][number] | undefined =>
  draft.projectById.estimates.find((e) => e.id === estimateId);

export function applyOptimisticAddEstimateSection(
  data: GetProjectEstimatesQuery,
  variables: AddEstimateSectionOptimisticVariables,
  context: OptimisticEstimateContext
): GetProjectEstimatesQuery {
  return produce(data, (draft) => {
    const estimate = findEstimate(draft, context.estimateId);
    if (!estimate) return;
    estimate.sections.push({
      id: variables.publicId,
      heading: variables.heading,
      totalIncTax: { value: 0, currency: estimate.totalIncTax.currency },
      lineItems: [],
    });
  });
}

export function applyOptimisticRemoveEstimateSection(
  data: GetProjectEstimatesQuery,
  variables: { id: string },
  context: OptimisticEstimateContext
): GetProjectEstimatesQuery {
  return produce(data, (draft) => {
    const estimate = findEstimate(draft, context.estimateId);
    if (!estimate) return;
    const idx = estimate.sections.findIndex((s) => s.id === variables.id);
    if (idx !== -1) estimate.sections.splice(idx, 1);
  });
}

export function applyOptimisticReorderEstimateLineItems(
  data: GetProjectEstimatesQuery,
  variables: ReorderEstimateLineItemsOptimisticVariables,
  context: OptimisticEstimateContext
): GetProjectEstimatesQuery {
  return produce(data, (draft) => {
    const estimate = findEstimate(draft, context.estimateId);
    if (!estimate) return;
    const section = estimate.sections.find((s) => s.id === variables.sectionId);
    if (!section) return;

    const byId = new Map(section.lineItems.map((li) => [li.id, li]));
    const reordered = variables.lineItemIds
      .map((id) => byId.get(id))
      .filter((li): li is NonNullable<typeof li> => li != null);
    // Any cached line item not listed in `lineItemIds` is appended so the
    // optimistic write is non-destructive.
    const referenced = new Set(variables.lineItemIds);
    const trailing = section.lineItems.filter((li) => !referenced.has(li.id));
    section.lineItems = [...reordered, ...trailing];
  });
}

export function applyOptimisticReorderEstimateSections(
  data: GetProjectEstimatesQuery,
  variables: ReorderEstimateSectionsOptimisticVariables,
  context: OptimisticEstimateContext
): GetProjectEstimatesQuery {
  return produce(data, (draft) => {
    const estimate = findEstimate(draft, context.estimateId);
    if (!estimate) return;

    const byId = new Map(estimate.sections.map((s) => [s.id, s]));
    const reordered = variables.sectionIds
      .map((id) => byId.get(id))
      .filter((s): s is NonNullable<typeof s> => s != null);
    // Any cached section not listed in `sectionIds` is appended so the
    // optimistic write is non-destructive.
    const referenced = new Set(variables.sectionIds);
    const trailing = estimate.sections.filter((s) => !referenced.has(s.id));
    estimate.sections = [...reordered, ...trailing];
  });
}

export function applyOptimisticUpdateEstimateSection(
  data: GetProjectEstimatesQuery,
  variables: UpdateEstimateSectionInput,
  context: OptimisticEstimateContext
): GetProjectEstimatesQuery {
  return produce(data, (draft) => {
    const estimate = findEstimate(draft, context.estimateId);
    if (!estimate) return;
    const section = estimate.sections.find((s) => s.id === variables.id);
    if (section) section.heading = variables.heading;
  });
}

/**
 * Materials drive a markup-priced line's recomputed price server-side, so there
 * is no reliable client-side optimistic patch. Pass the cache through unchanged
 * and let the mutation's response hydrate it. The wrapper is still used so the
 * write shares the per-estimate mutation scope and serialises with the
 * accompanying line item update.
 */
export function applyOptimisticUpdateEstimateLineItemMaterials(
  data: GetProjectEstimatesQuery,
  _variables: UpdateEstimateLineItemMaterialsInput,
  _context: OptimisticEstimateContext
): GetProjectEstimatesQuery {
  return data;
}

export function applyOptimisticUpdateEstimateLineItem(
  data: GetProjectEstimatesQuery,
  variables: UpdateEstimateLineItemInput,
  context: OptimisticEstimateContext
): GetProjectEstimatesQuery {
  // Patch the line item's explicit fields. Don't recompute totals or resolve
  // taxRate here — tax inclusivity/reverse-charge rules make a correct
  // client-side calculation fragile; the server's response hydrates the cache
  // on success.
  return produce(data, (draft) => {
    const estimate = findEstimate(draft, context.estimateId);
    if (!estimate) return;
    for (const section of estimate.sections) {
      const lineItem = section.lineItems.find((li) => li.id === variables.id);
      if (!lineItem) continue;
      if (variables.internalReference !== undefined) {
        lineItem.internalReference = variables.internalReference ?? '';
      }
      if (variables.description !== undefined) {
        lineItem.description = variables.description;
      }
      if (variables.quantity != null) {
        lineItem.quantity = variables.quantity;
      }
      if (variables.unitPriceExcTax != null) {
        lineItem.unitPriceExcTax.value = variables.unitPriceExcTax;
      }
      return;
    }
  });
}

export function applyOptimisticRemoveEstimateLineItem(
  data: GetProjectEstimatesQuery,
  variables: { id: string },
  context: OptimisticEstimateContext
): GetProjectEstimatesQuery {
  return produce(data, (draft) => {
    const estimate = findEstimate(draft, context.estimateId);
    if (!estimate) return;
    const removed = estimate.sections
      .flatMap((s) => s.lineItems)
      .find((li) => li.id === variables.id);
    if (!removed) return;
    const removedIncTaxValue = removed.totalIncTax.value;
    estimate.totalIncTax.value -= removedIncTaxValue;
    for (const section of estimate.sections) {
      const idx = section.lineItems.findIndex((li) => li.id === variables.id);
      if (idx === -1) continue;
      section.totalIncTax.value -= removedIncTaxValue;
      section.lineItems.splice(idx, 1);
    }
  });
}
